import { queryClient } from './queryClient';
import { queryKeys } from './queryKeys';
import apiClient from './apiClient';
import { buildPromptListPath, defaultPromptListParams } from './promptListQuery';
import { defaultPlaybookListParams } from '@/types/playbooks';
import { playbookService } from '@/services/playbooks';
import { STALE } from './queryPolicy';

function pre(queryKey: readonly unknown[], path: string) {
  queryClient.prefetchQuery({
    queryKey,
    queryFn: () => apiClient.get(path),
    staleTime: STALE.standard,
  });
}

const prefetchers: Record<string, (wid: string) => void> = {
  '/overview': (wid) => {
    pre(queryKeys.coverage.detail(wid), `/workspaces/${wid}/coverage`);
    pre(queryKeys.periods.trend(wid), `/workspaces/${wid}/periods/trend`);
  },
  '/prompts': (wid) => {
    // First page of the server-paginated library, keyed exactly like
    // PromptFlatTab's initial workspace-wide request.
    queryClient.prefetchInfiniteQuery({
      queryKey: queryKeys.prompts.list(wid, defaultPromptListParams()),
      queryFn: () => apiClient.get(buildPromptListPath(wid, defaultPromptListParams(), 0)),
      initialPageParam: 0,
      staleTime: STALE.standard,
    });
    pre(queryKeys.company.detail(wid), `/workspaces/${wid}/company`);
    pre(queryKeys.subjects.list(wid), `/workspaces/${wid}/subjects`);
    pre(queryKeys.company.products(wid), `/workspaces/${wid}/products`);
    pre(['most-cited-sources', wid], `/workspaces/${wid}/insights/most-cited-sources`);
    pre(queryKeys.personas.list(wid), `/workspaces/${wid}/company/personas`);
  },
  '/facts/fact-ledger': (wid) => {
    pre(queryKeys.company.detail(wid), `/workspaces/${wid}/company`);
    pre(queryKeys.subjects.list(wid), `/workspaces/${wid}/subjects`);
    pre(queryKeys.personas.list(wid), `/workspaces/${wid}/company/personas`);
  },
  '/benchmarks': (wid) => {
    pre(queryKeys.company.detail(wid), `/workspaces/${wid}/company`);
  },
  '/content/playbooks': (wid) => {
    // Match the page's infinite-query key and first-page shape exactly.
    const params = defaultPlaybookListParams();
    queryClient.prefetchInfiniteQuery({
      queryKey: queryKeys.playbooks.list(wid, params),
      queryFn: ({ pageParam }) => playbookService.list(wid, { ...params, offset: Number(pageParam ?? 0) }),
      initialPageParam: 0,
      staleTime: STALE.standard,
    });
  },
  '/intelligence/issues': (wid) => {
    // First page of the server-paginated + server-filtered issue list, keyed
    // exactly like IssuesPage's initial (no filters, default sort) query.
    const defaultParams = { type: [], competitor: [], search: '', sort: '', direction: 'desc' };
    queryClient.prefetchInfiniteQuery({
      queryKey: queryKeys.issues.list(wid, defaultParams),
      queryFn: () => apiClient.get(`/workspaces/${wid}/issues?limit=20&offset=0`),
      initialPageParam: 0,
      staleTime: STALE.standard,
    });
  },

};

const chunkLoaders: Record<string, () => Promise<unknown>> = {
  '/overview': () => import('../pages/console/home/OverviewPage'),
  '/prompts': () => import('../pages/console/prompts/QuestionsPage'),
  '/intelligence/query-fan-out': () => import('../pages/console/prompts/QueryFanoutPage'),
  '/facts/fact-ledger': () => import('../pages/console/company-record/FactLedgerPage'),
  '/facts/to-approve': () => import('../pages/console/company-record/DiscoveriesPage'),
  '/intelligence/sources': () => import('../pages/console/sources/SourcesPage'),
  '/benchmarks': () => import('../pages/console/benchmarks/BenchmarksPage'),
  '/content/playbooks': () => import('../pages/console/playbook/PlaybookListPage'),
  '/settings': () => import('../pages/console/admin/SettingsLayout'),
  '/intelligence/issues': () => import('../pages/console/issues/IssuesPage'),
};

export function prefetchPromptHistory(workspaceId: string, promptId: string) {
  pre(
    queryKeys.prompts.history(workspaceId, promptId),
    `/workspaces/${workspaceId}/workspace-prompts/${promptId}/history`,
  );
  import('../pages/console/evaluations/QuestionDetailPage');
}

export function prefetchEvaluation(workspaceId: string, evaluationId: string) {
  pre(
    queryKeys.evaluations.detail(workspaceId, evaluationId),
    `/workspaces/${workspaceId}/evaluations/${evaluationId}`,
  );
  import('../pages/console/evaluations/AnswerDetailPage');
}

export function prefetchCompetitor(workspaceId: string, competitorId: string) {
  pre(
    queryKeys.competitors.detail(workspaceId, competitorId),
    `/workspaces/${workspaceId}/competitors/${competitorId}`,
  );
  import('../pages/console/context/CompetitorDetailPage');
}

export function prefetchIssue(workspaceId: string, issueId: string) {
  pre(queryKeys.issues.detail(workspaceId, issueId), `/workspaces/${workspaceId}/issues/${issueId}`);
  import('../pages/console/issues/IssueDetailPage');
}

export function prefetchPlaybook(workspaceId: string, playbookId: string) {
  pre(queryKeys.playbooks.detail(workspaceId, playbookId), `/workspaces/${workspaceId}/playbooks/${playbookId}`);
  import('../pages/console/playbook/PlaybookDetailPage');
}

export function prefetchTopic(workspaceId: string, topicId: string) {
  pre(queryKeys.topics.detail(workspaceId, topicId), `/workspaces/${workspaceId}/topics/${topicId}`);
  import('../pages/console/prompts/TopicDetailPage');
}

/** Prefetches the three queries PeriodDetailPage reads for a given period
 *  (detail, metrics, measurement — brand-scoped, matching the page's default
 *  `subjectId` of `undefined`). Wired on the prev/next chevron hover so
 *  paging through periods is instant. The page's own chunk is already loaded
 *  (the caller IS PeriodDetailPage), so no `import()` here. */
export function prefetchPeriod(workspaceId: string, periodId: string) {
  pre(queryKeys.periods.detail(workspaceId, periodId), `/workspaces/${workspaceId}/periods/${periodId}`);
  pre(
    queryKeys.periods.metrics(workspaceId, periodId, undefined),
    `/workspaces/${workspaceId}/periods/${periodId}/metrics`,
  );
  pre(
    queryKeys.periods.measurement(workspaceId, periodId, undefined),
    `/workspaces/${workspaceId}/periods/${periodId}/measurement`,
  );
}

// prefetchPersona/prefetchProduct/prefetchTheme are intentionally NOT defined:
// PersonaDetailPage, ProductDetailPage, and ThemeDetailPage all derive their
// header identity from the already-fetched LIST query (personas/subjects/
// themes respectively) rather than a per-entity detail endpoint — the list
// tables that would wire row hover already hold that same query in cache by
// the time a row renders, so a hover prefetch would just re-request data the
// page doesn't have a distinct key for. Nothing to warm.

export function prefetchRoute(href: string, workspaceId: string | null) {
  if (!workspaceId) return;
  const pathname = href.split('?', 1)[0];

  const route = Object.keys(prefetchers).find((r) => pathname === r || pathname.startsWith(r + '/'));
  if (route) prefetchers[route](workspaceId);

  const chunk = Object.keys(chunkLoaders).find((r) => pathname === r || pathname.startsWith(r + '/'));
  if (chunk) chunkLoaders[chunk]();
}
